import { h } from '@stencil/core';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import { DEBOUNCED_HANDLER_DELAY, sleep } from '@pes-ui/utils';
import { applyEllipsisByMaxHeight, getTrimmedContentByLength } from './utils';
export class Ellipsis {
  constructor() {
    this.update = () => {
      return sleep().then(() => {
        var _a;
        applyEllipsisByMaxHeight(this.ref, this.trimmedContentByLength);
        this.isTruncated = ((_a = this.ref) === null || _a === void 0 ? void 0 : _a.innerHTML) !== this.content;
      });
    };
    this.debouncedUpdate = debounce(this.update, DEBOUNCED_HANDLER_DELAY);
    this.content = undefined;
    this.maxHeight = undefined;
    this.modifier = undefined;
    this.wrapperModifier = undefined;
    this.maxLength = undefined;
    this.hasTooltip = false;
    this.trimmedContentByLength = undefined;
    this.isTruncated = false;
    this.isPdpDesc = false;
  }
  contentOrMaxLengthDidChange() {
    this.updateTrimmedContentByLength();
  }
  triggerUpdate() {
    return this.update();
  }
  updateTrimmedContentByLength() {
    this.trimmedContentByLength = getTrimmedContentByLength(this.content, this.maxLength);
  }
  componentWillLoad() {
    this.updateTrimmedContentByLength();
  }
  componentDidLoad() {
    window.addEventListener('resize', this.debouncedUpdate);
    return this.update();
  }
  componentDidUpdate() {
    return this.update();
  }
  disconnectedCallback() {
    window.removeEventListener('resize', this.debouncedUpdate);
  }
  render() {
    var _a;
    return (h("pes-tooltip-wrapper", { showTooltip: this.hasTooltip && this.isTruncated, class: cn('tooltip-wrapper', this.wrapperModifier && `tooltip-wrapper-${this.wrapperModifier}`) }, h("div", { slot: "tooltip-activator", ref: (el) => (this.ref = el), class: cn(this.modifier && `pes-ellipsis-${this.modifier}`, { 'pes-ellipsis': !this.isPdpDesc }, {
        'pdp-long-desc': this.isPdpDesc,
      }), style: { maxHeight: (_a = this.maxHeight) !== null && _a !== void 0 ? _a : 'none' } }), this.hasTooltip && h("div", { slot: "tooltip-content", innerHTML: this.content })));
  }
  static get is() { return "pes-ellipsis"; }
  static get encapsulation() { return "shadow"; }
  static get originalStyleUrls() {
    return {
      "$": ["ellipsis.scss"]
    };
  }
  static get styleUrls() {
    return {
      "$": ["ellipsis.css"]
    };
  }
  static get properties() {
    return {
      "content": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": true,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Text to truncate and add an ellipsis."
        },
        "attribute": "content",
        "reflect": false
      },
      "maxHeight": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Max height of the container."
        },
        "attribute": "max-height",
        "reflect": false
      },
      "modifier": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Css class modifier of the container."
        },
        "attribute": "modifier",
        "reflect": false
      },
      "wrapperModifier": {
        "type": "string",
        "mutable": false,
        "complexType": {
          "original": "string",
          "resolved": "string",
          "references": {}
        },
        "required": false,
        "optional": true,
        "docs": {
          "tags": [],
          "text": "Css class modifier of the wrapper."
        },
        "attribute": "wrapper-modifier",
        "reflect": false
      },
      "maxLength": {
        "type": "number",
        "mutable": false,
        "complexType": {
          "original": "number",
          "resolved": "number",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": "Max length of the visible content"
        },
        "attribute": "max-length",
        "reflect": false
      },
      "hasTooltip": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "has-tooltip",
        "reflect": false,
        "defaultValue": "false"
      },
      "isPdpDesc": {
        "type": "boolean",
        "mutable": false,
        "complexType": {
          "original": "boolean",
          "resolved": "boolean",
          "references": {}
        },
        "required": false,
        "optional": false,
        "docs": {
          "tags": [],
          "text": ""
        },
        "attribute": "is-pdp-desc",
        "reflect": false,
        "defaultValue": "false"
      }
    };
  }
  static get states() {
    return {
      "trimmedContentByLength": {},
      "isTruncated": {}
    };
  }
  static get methods() {
    return {
      "triggerUpdate": {
        "complexType": {
          "signature": "() => Promise<void>",
          "parameters": [],
          "references": {
            "Promise": {
              "location": "global"
            }
          },
          "return": "Promise<void>"
        },
        "docs": {
          "text": "",
          "tags": []
        }
      }
    };
  }
  static get watchers() {
    return [{
        "propName": "content",
        "methodName": "contentOrMaxLengthDidChange"
      }, {
        "propName": "maxLength",
        "methodName": "contentOrMaxLengthDidChange"
      }];
  }
}
